import React, { useState, useEffect } from 'react';
import { getDashboardStats } from '../../services/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  HiUsers, HiShoppingBag, HiCurrencyRupee, HiClock,
  HiDocumentText, HiExclamationTriangle, HiCube, HiUserGroup,
} from 'react-icons/hi2';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);

const ORDER_STATUS_CLASS = {
  pending: 'badge-yellow',
  confirmed: 'badge-blue',
  dispatched: 'badge-purple',
  delivered: 'badge-green',
  cancelled: 'badge-red',
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getDashboardStats()
      .then((res) => setStats(res.data))
      .catch(() => setError('Failed to load dashboard stats.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="loading-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return <div className="content-area"><div className="alert alert-error">{error}</div></div>;
  }

  const {
    total_customers = 0,
    total_orders = 0,
    total_revenue = 0,
    pending_orders = 0,
    unpaid_invoices = 0,
    total_due = 0,
    low_stock_items = 0,
    active_employees = 0,
    monthly_revenue = [],
    top_products = [],
    recent_orders = [],
  } = stats || {};

  const topStats = [
    { label: 'Total Customers', value: fmt(total_customers), icon: HiUsers, color: '#0ea5e9' },
    { label: 'Total Orders', value: fmt(total_orders), icon: HiShoppingBag, color: '#8b5cf6' },
    { label: 'Total Revenue', value: `₹${fmt(total_revenue)}`, icon: HiCurrencyRupee, color: '#22c55e' },
    { label: 'Pending Orders', value: fmt(pending_orders), icon: HiClock, color: '#f59e0b' },
  ];

  const bottomStats = [
    { label: 'Unpaid Invoices', value: fmt(unpaid_invoices), icon: HiDocumentText, color: '#ef4444' },
    { label: 'Total Due', value: `₹${fmt(total_due)}`, icon: HiCurrencyRupee, color: '#ef4444' },
    { label: 'Low Stock Items', value: fmt(low_stock_items), icon: HiExclamationTriangle, color: '#f59e0b' },
    { label: 'Active Employees', value: fmt(active_employees), icon: HiUserGroup, color: '#0ea5e9' },
  ];

  return (
    <div className="content-area">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-4" style={{ marginBottom: '1rem' }}>
        {topStats.map((s) => (
          <div className="stat-card" key={s.label}>
            <div className="flex-between">
              <span className="stat-label">{s.label}</span>
              <s.icon style={{ color: s.color, fontSize: '1.5rem' }} />
            </div>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Bottom stat cards */}
      <div className="grid grid-4" style={{ marginBottom: '1.5rem' }}>
        {bottomStats.map((s) => (
          <div className="stat-card" key={s.label}>
            <div className="flex-between">
              <span className="stat-label">{s.label}</span>
              <s.icon style={{ color: s.color, fontSize: '1.5rem' }} />
            </div>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Monthly Revenue Chart */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <span className="card-title">Monthly Revenue (Last 12 Months)</span>
        </div>
        <div className="card-body">
          {monthly_revenue.length === 0 ? (
            <p className="text-muted" style={{ textAlign: 'center', padding: '2rem 0' }}>No data available.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={monthly_revenue} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v, name) => [name === 'revenue' ? `₹${fmt(v)}` : v, name === 'revenue' ? 'Revenue' : 'Orders']} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#0ea5e9" strokeWidth={2} dot={false} name="Revenue" />
                <Line type="monotone" dataKey="orders" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Orders" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-2">
        {/* Top Products */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Top Products</span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Qty Sold</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {top_products.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>No data</td></tr>
                ) : (
                  top_products.map((p, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{p.name}</td>
                      <td>{fmt(p.total_qty)}</td>
                      <td>₹{fmt(p.revenue)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Recent Orders</span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recent_orders.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8' }}>No data</td></tr>
                ) : (
                  recent_orders.map((o, i) => (
                    <tr key={i}>
                      <td>{o.order_number}</td>
                      <td>{o.customer_name}</td>
                      <td>
                        <span className={`badge ${ORDER_STATUS_CLASS[o.status] || 'badge-gray'}`}>
                          {o.status}
                        </span>
                      </td>
                      <td>₹{fmt(o.total_amount)}</td>
                      <td>{o.order_date ? new Date(o.order_date).toLocaleDateString('en-IN') : '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
